import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, CircularProgress,
  Alert, Stack, Chip, Checkbox, Collapse, FormControlLabel, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, MenuItem, Select, FormControl, LinearProgress,
} from '@mui/material';
import {
  RecordVoiceOver as TranscriptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as AcceptIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Token as TokenIcon,
  UploadFile as BatchUploadIcon,
} from '@mui/icons-material';
import { transcriptsApi, calendarApi } from '../api/services';
import type { MeetingTranscript, TranscriptExtraction, CalendarEvent } from '../types/models';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

const PRIORITY_COLORS: Record<string, string> = {
  Critical: COLORS.error,
  High: COLORS.secondary,
  Medium: COLORS.primary,
  Low: COLORS.success,
};

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  Uploaded: { bg: 'rgba(136, 146, 168, 0.15)', color: '#8892A8' },
  Processing: { bg: 'rgba(108, 155, 255, 0.15)', color: '#6C9BFF' },
  Analysed: { bg: 'rgba(105, 240, 174, 0.15)', color: '#69F0AE' },
  Error: { bg: 'rgba(255, 82, 82, 0.15)', color: '#FF5252' },
};

function ConfidenceBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  let color = COLORS.error;
  let bg = 'rgba(255, 82, 82, 0.15)';
  if (score > 0.8) {
    color = COLORS.success;
    bg = 'rgba(105, 240, 174, 0.15)';
  } else if (score >= 0.6) {
    color = COLORS.warning;
    bg = 'rgba(255, 183, 77, 0.15)';
  }
  return (
    <Chip label={`${pct}%`} size="small" sx={{ bgcolor: bg, color, fontWeight: 600, minWidth: 48 }} />
  );
}

function TypeChip({ type }: { type: string }) {
  const typeColors: Record<string, string> = {
    Task: '#6C9BFF', Decision: '#FF8A65', Briefing: '#69F0AE', Review: '#FFB74D',
    FollowUp: '#CE93D8', Draft: '#4DD0E1', Reading: '#AED581',
  };
  const c = typeColors[type] || COLORS.textSecondary;
  return <Chip label={type} size="small" sx={{ bgcolor: `${c}20`, color: c, fontWeight: 600 }} />;
}

function PriorityChipLocal({ priority }: { priority: string }) {
  const c = PRIORITY_COLORS[priority] || COLORS.textSecondary;
  return <Chip label={priority} size="small" sx={{ bgcolor: `${c}20`, color: c, fontWeight: 600 }} />;
}

export function TranscriptsPage() {
  // Upload form state
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [speakerLabels, setSpeakerLabels] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Batch upload state
  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchProgress, setBatchProgress] = useState(0);

  // Calendar auto-link state
  const [matchingEvents, setMatchingEvents] = useState<CalendarEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  // Transcripts list state
  const [transcripts, setTranscripts] = useState<MeetingTranscript[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [expandedSummary, setExpandedSummary] = useState<Record<string, boolean>>({});
  const [selectedExtractions, setSelectedExtractions] = useState<Record<string, Set<string>>>({});
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [acceptSuccess, setAcceptSuccess] = useState<Record<string, string>>({});

  // Confidence filter
  const [highConfidenceOnly, setHighConfidenceOnly] = useState(false);

  // Inline editing
  const [editingExtraction, setEditingExtraction] = useState<Record<string, Partial<TranscriptExtraction>>>({});

  const loadTranscripts = useCallback(async () => {
    try {
      const data = await transcriptsApi.getAll();
      setTranscripts(data);
    } catch (err) {
      console.error('Failed to load transcripts', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadTranscripts();
  }, [loadTranscripts]);

  // Auto-link: when meeting date changes, search for matching calendar events
  useEffect(() => {
    if (!meetingDate) {
      setMatchingEvents([]);
      setSelectedEventId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const events = await calendarApi.getByDate(meetingDate);
        if (!cancelled) {
          setMatchingEvents(events);
          // Pre-fill title and attendees from the first matching event if fields are empty
          if (events.length > 0 && !title.trim()) {
            setTitle(events[0].title);
            setAttendees(events[0].attendees || '');
            setSelectedEventId(events[0].id);
          }
        }
      } catch {
        if (!cancelled) setMatchingEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [meetingDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle linking to a specific calendar event
  const handleEventLink = (eventId: string) => {
    setSelectedEventId(eventId);
    const event = matchingEvents.find(e => e.id === eventId);
    if (event) {
      setTitle(event.title);
      setAttendees(event.attendees || '');
    }
  };

  // Batch upload handler
  const handleBatchUpload = async () => {
    if (batchFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setBatchProgress(0);
    let successCount = 0;
    let errorCount = 0;
    for (let i = 0; i < batchFiles.length; i++) {
      try {
        const file = batchFiles[i];
        const text = await file.text();
        const fileTitle = file.name.replace(/\.[^.]+$/, '');
        const transcript = await transcriptsApi.upload({
          title: fileTitle,
          meetingDate: new Date().toISOString().split('T')[0],
          transcriptText: text,
        });
        await transcriptsApi.analyse(transcript.id);
        successCount++;
      } catch {
        errorCount++;
      }
      setBatchProgress(Math.round(((i + 1) / batchFiles.length) * 100));
    }
    if (errorCount > 0) {
      setUploadError(`${errorCount} of ${batchFiles.length} files failed to upload.`);
    }
    if (successCount > 0) {
      setUploadSuccess(`${successCount} transcript(s) uploaded and analysed successfully.`);
    }
    setBatchFiles([]);
    setBatchProgress(0);
    setUploading(false);
    await loadTranscripts();
  };

  const handleUploadAndAnalyse = async () => {
    if (!title.trim() || !meetingDate || !transcriptText.trim()) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const transcript = await transcriptsApi.upload({
        title: title.trim(),
        meetingDate,
        attendees: attendees.trim() || undefined,
        transcriptText: transcriptText.trim(),
      });

      // Immediately trigger analysis
      await transcriptsApi.analyse(transcript.id);

      setUploadSuccess('Transcript uploaded and analysed successfully.');
      setTitle('');
      setMeetingDate('');
      setAttendees('');
      setSpeakerLabels('');
      setTranscriptText('');

      // Reload the list
      await loadTranscripts();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Failed to upload/analyse transcript');
    } finally {
      setUploading(false);
    }
  };

  const toggleSummary = (id: string) => {
    setExpandedSummary(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleExtraction = (transcriptId: string, extractionId: string) => {
    setSelectedExtractions(prev => {
      const current = new Set(prev[transcriptId] || []);
      if (current.has(extractionId)) {
        current.delete(extractionId);
      } else {
        current.add(extractionId);
      }
      return { ...prev, [transcriptId]: current };
    });
  };

  const toggleAllExtractions = (transcriptId: string, extractions: TranscriptExtraction[]) => {
    setSelectedExtractions(prev => {
      const current = prev[transcriptId] || new Set();
      const allSelected = extractions.every(e => current.has(e.id));
      if (allSelected) {
        return { ...prev, [transcriptId]: new Set() };
      } else {
        return { ...prev, [transcriptId]: new Set(extractions.map(e => e.id)) };
      }
    });
  };

  const handleAccept = async (transcriptId: string) => {
    const selected = selectedExtractions[transcriptId];
    if (!selected || selected.size === 0) return;

    setAcceptingId(transcriptId);
    try {
      await transcriptsApi.accept(transcriptId, Array.from(selected));
      setAcceptSuccess(prev => ({ ...prev, [transcriptId]: `${selected.size} work item(s) created successfully.` }));
      setSelectedExtractions(prev => ({ ...prev, [transcriptId]: new Set() }));
      await loadTranscripts();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Failed to accept extractions');
    } finally {
      setAcceptingId(null);
    }
  };

  const getStatusStyle = (status: string) => STATUS_STYLES[status] || STATUS_STYLES.Uploaded;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <TranscriptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h4">Transcripts</Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        Upload meeting transcripts to extract action items using AI
      </Typography>

      {/* Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Upload Transcript</Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={batchMode}
                  onChange={e => setBatchMode(e.target.checked)}
                  disabled={uploading}
                />
              }
              label={<Typography variant="body2">Batch Mode</Typography>}
            />
          </Box>

          {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>{uploadError}</Alert>}
          {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>{uploadSuccess}</Alert>}

          {batchMode ? (
            <Stack spacing={2.5}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Select multiple .txt or .md files. Each file's name will be used as the transcript title and today's date as the meeting date.
              </Alert>
              <Button
                variant="outlined"
                component="label"
                startIcon={<BatchUploadIcon />}
                disabled={uploading}
              >
                {batchFiles.length > 0 ? `${batchFiles.length} file(s) selected` : 'Choose Files'}
                <input
                  type="file"
                  multiple
                  accept=".txt,.md"
                  hidden
                  onChange={e => setBatchFiles(Array.from(e.target.files || []))}
                />
              </Button>
              {batchFiles.length > 0 && (
                <Stack spacing={0.5}>
                  {batchFiles.map((f, i) => (
                    <Typography key={i} variant="body2" sx={{ color: COLORS.textSecondary }}>
                      {f.name} ({(f.size / 1024).toFixed(1)} KB)
                    </Typography>
                  ))}
                </Stack>
              )}
              {uploading && batchProgress > 0 && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress variant="determinate" value={batchProgress} sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                    {batchProgress}% complete
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                  onClick={handleBatchUpload}
                  disabled={uploading || batchFiles.length === 0}
                  size="large"
                  sx={{ minWidth: 200 }}
                >
                  {uploading ? `Uploading ${batchFiles.length} files...` : `Upload & Analyse (${batchFiles.length})`}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  fullWidth
                  required
                  disabled={uploading}
                  placeholder="e.g. Weekly Leadership Sync"
                />
                <TextField
                  label="Meeting Date"
                  type="date"
                  value={meetingDate}
                  onChange={e => setMeetingDate(e.target.value)}
                  fullWidth
                  required
                  disabled={uploading}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>
              {/* Link to calendar event dropdown */}
              {matchingEvents.length > 0 && (
                <FormControl fullWidth>
                  <Select
                    value={selectedEventId}
                    onChange={e => handleEventLink(e.target.value)}
                    displayEmpty
                    size="small"
                    disabled={uploading}
                  >
                    <MenuItem value="">
                      <em>Link to meeting (optional) — {matchingEvents.length} event(s) found</em>
                    </MenuItem>
                    {matchingEvents.map(evt => (
                      <MenuItem key={evt.id} value={evt.id}>
                        {evt.title} ({new Date(evt.startTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label="Attendees"
                value={attendees}
                onChange={e => setAttendees(e.target.value)}
                fullWidth
                disabled={uploading}
                placeholder="e.g. John Smith, Jane Doe, Mark Wilson"
                helperText="Optional — comma-separated names"
              />
              <TextField
                label="Speaker Labels"
                value={speakerLabels}
                onChange={e => setSpeakerLabels(e.target.value)}
                fullWidth
                disabled={uploading}
                placeholder="e.g. Speaker 1: John, Speaker 2: Jane"
                helperText="Optional — helps AI attribute action items to the correct person"
              />
              <TextField
                label="Transcript Text"
                value={transcriptText}
                onChange={e => setTranscriptText(e.target.value)}
                fullWidth
                required
                multiline
                rows={8}
                disabled={uploading}
                placeholder="Paste the full meeting transcript here..."
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                  onClick={handleUploadAndAnalyse}
                  disabled={uploading || !title.trim() || !meetingDate || !transcriptText.trim()}
                  size="large"
                  sx={{ minWidth: 200 }}
                >
                  {uploading ? 'Analysing transcript with AI...' : 'Upload & Analyse'}
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Past Transcripts */}
      <Typography variant="h5" sx={{ mb: 2 }}>Past Transcripts</Typography>

      {loadingList && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingList && transcripts.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <TranscriptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No transcripts yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload your first meeting transcript above to get started.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {transcripts.map(transcript => {
          const statusStyle = getStatusStyle(transcript.status);
          const selected = selectedExtractions[transcript.id] || new Set();
          const extractions = transcript.extractions || [];
          const isAnalysed = transcript.status === 'Analysed';
          const allSelected = extractions.length > 0 && extractions.every(e => selected.has(e.id));
          const hasAcceptSuccess = acceptSuccess[transcript.id];

          return (
            <Card key={transcript.id} sx={{ borderLeft: 3, borderColor: statusStyle.color }}>
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Typography variant="h6" sx={{ color: COLORS.textPrimary }}>{transcript.title}</Typography>
                      <Chip
                        label={transcript.status}
                        size="small"
                        sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }}
                      />
                    </Box>
                    <Stack direction="row" spacing={2}>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        {new Date(transcript.meetingDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      {transcript.attendees && (
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                          {transcript.attendees}
                        </Typography>
                      )}
                      {transcript.tokensUsed != null && (
                        <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                          {transcript.tokensUsed.toLocaleString()} tokens used
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>

                {/* AI Summary */}
                {transcript.aiSummary && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => toggleSummary(transcript.id)}
                      endIcon={expandedSummary[transcript.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ color: COLORS.textSecondary, textTransform: 'none', px: 0 }}
                    >
                      AI Summary
                    </Button>
                    <Collapse in={expandedSummary[transcript.id]}>
                      <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(108, 155, 255, 0.06)', borderRadius: 2, border: '1px solid rgba(108, 155, 255, 0.1)' }}>
                        <Typography variant="body2" sx={{ color: COLORS.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                          {transcript.aiSummary}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* Accept Success */}
                {hasAcceptSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }} onClose={() => setAcceptSuccess(prev => { const n = { ...prev }; delete n[transcript.id]; return n; })}>
                    {hasAcceptSuccess}
                  </Alert>
                )}

                {/* Extractions Table */}
                {isAnalysed && extractions.length > 0 && (() => {
                  const filteredExtractions = highConfidenceOnly
                    ? extractions.filter(e => e.confidenceScore > 0.8)
                    : extractions;
                  const filteredAllSelected = filteredExtractions.length > 0 && filteredExtractions.every(e => selected.has(e.id));

                  return (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle2" sx={{ color: COLORS.textSecondary }}>
                          Extracted Action Items ({filteredExtractions.length}{highConfidenceOnly ? ` of ${extractions.length}` : ''})
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={highConfidenceOnly}
                              onChange={e => setHighConfidenceOnly(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                              High confidence only (&gt;80%)
                            </Typography>
                          }
                          sx={{ ml: 0 }}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={acceptingId === transcript.id ? <CircularProgress size={16} color="inherit" /> : <AcceptIcon />}
                        onClick={() => handleAccept(transcript.id)}
                        disabled={selected.size === 0 || acceptingId === transcript.id}
                        color="success"
                      >
                        Accept Selected ({selected.size})
                      </Button>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ borderColor: COLORS.gridLine }}>
                              <Checkbox
                                checked={filteredAllSelected}
                                indeterminate={selected.size > 0 && !filteredAllSelected}
                                onChange={() => toggleAllExtractions(transcript.id, filteredExtractions)}
                                size="small"
                                sx={{ color: COLORS.textSecondary, '&.Mui-checked': { color: COLORS.primary } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Title</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Priority</TableCell>
                            <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Duration</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Assigned To</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Due Context</TableCell>
                            <TableCell align="center" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Confidence</TableCell>
                            <TableCell align="center" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, width: 40 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredExtractions.map(ext => {
                            const isEditing = !!editingExtraction[ext.id];
                            const edited = editingExtraction[ext.id] || {};
                            return (
                            <TableRow
                              key={ext.id}
                              sx={{
                                bgcolor: selected.has(ext.id) ? 'rgba(108, 155, 255, 0.06)' : 'transparent',
                                '& td': { borderColor: COLORS.gridLine },
                                opacity: ext.wasAccepted ? 0.5 : 1,
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selected.has(ext.id)}
                                  onChange={() => toggleExtraction(transcript.id, ext.id)}
                                  size="small"
                                  disabled={ext.wasAccepted}
                                  sx={{ color: COLORS.textSecondary, '&.Mui-checked': { color: COLORS.primary } }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: COLORS.textPrimary }}>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    value={edited.title ?? ext.title}
                                    onChange={e => setEditingExtraction(prev => ({ ...prev, [ext.id]: { ...prev[ext.id], title: e.target.value } }))}
                                    variant="standard"
                                    sx={{ '& input': { color: COLORS.textPrimary, fontSize: '0.875rem' } }}
                                  />
                                ) : (
                                  <>
                                    {ext.title}
                                    {ext.description && (
                                      <Typography variant="caption" sx={{ display: 'block', color: COLORS.textSecondary, mt: 0.25 }}>
                                        {ext.description}
                                      </Typography>
                                    )}
                                  </>
                                )}
                                {ext.wasAccepted && (
                                  <Chip label="Accepted" size="small" sx={{ ml: 1, bgcolor: 'rgba(105, 240, 174, 0.15)', color: COLORS.success, fontSize: '0.65rem', height: 18 }} />
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                    <Select
                                      value={edited.workItemType ?? ext.workItemType}
                                      onChange={e => setEditingExtraction(prev => ({ ...prev, [ext.id]: { ...prev[ext.id], workItemType: e.target.value } }))}
                                      sx={{ color: COLORS.textPrimary, fontSize: '0.8rem' }}
                                    >
                                      {['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'].map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <TypeChip type={ext.workItemType} />
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <FormControl size="small" variant="standard" sx={{ minWidth: 75 }}>
                                    <Select
                                      value={edited.priority ?? ext.priority}
                                      onChange={e => setEditingExtraction(prev => ({ ...prev, [ext.id]: { ...prev[ext.id], priority: e.target.value } }))}
                                      sx={{ color: COLORS.textPrimary, fontSize: '0.8rem' }}
                                    >
                                      {['Critical', 'High', 'Medium', 'Low'].map(p => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <PriorityChipLocal priority={ext.priority} />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                                {ext.suggestedDurationMinutes}m
                              </TableCell>
                              <TableCell sx={{ color: ext.assignedTo ? COLORS.textPrimary : COLORS.textSecondary }}>
                                {ext.assignedTo || '--'}
                              </TableCell>
                              <TableCell sx={{ color: ext.dueContext ? COLORS.textPrimary : COLORS.textSecondary }}>
                                {ext.dueContext || '--'}
                              </TableCell>
                              <TableCell align="center">
                                <ConfidenceBadge score={ext.confidenceScore} />
                              </TableCell>
                              <TableCell align="center">
                                {!ext.wasAccepted && (
                                  isEditing ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingExtraction(prev => { const n = { ...prev }; delete n[ext.id]; return n; })}
                                      sx={{ color: COLORS.textSecondary }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingExtraction(prev => ({ ...prev, [ext.id]: {} }))}
                                      sx={{ color: COLORS.textSecondary }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )
                                )}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  );
                })()}

                {isAnalysed && extractions.length === 0 && (
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 2, fontStyle: 'italic' }}>
                    No action items were extracted from this transcript.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

export default TranscriptsPage;
